import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button } from 'react-bootstrap';

/**
 * ═══════════════════════════════════════════════════════════════
 * ADVANCED HEALTH PREDICTION & ASSESSMENT SYSTEM - UPDATED v2.0
 * ═══════════════════════════════════════════════════════════════
 * 
 * NEW FEATURES:
 * ✓ Improved Padding & Margin throughout component
 * ✓ Disease Blog Display - Shows ALL disease predictions
 * ✓ Answer-based disease mapping
 * ✓ Generic, responsive layout
 * ✓ Specialist Recommendation System
 * ✓ Professional Medical Report Generation
 */

const THEME = {
    primary: '#10b981',
    primaryLight: '#34d399',
    primaryDark: '#059669',
    primarySoft: '#d1fae5',
    primarySofter: '#ecfdf5',
    secondary: '#111827',
    tertiary: '#1f2937',
    muted: '#6b7280',
    mutedLight: '#9ca3af',
    white: '#ffffff',
    backgroundLight: '#f9fafb',
    backgroundLighter: '#f3f4f6',
    surface: '#ffffff',
    danger: '#dc2626',
    warning: '#f59e0b',
    success: '#10b981',
    info: '#3b82f6',
    border: '#e5e7eb',
    borderLight: '#f3f4f6',
    borderDark: '#d1d5db',
    shadowSm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    shadowMd: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    shadowLg: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
};

/**
 * SPACING SYSTEM - Generic & Reusable
 */
const SPACING = {
    xs: '4px',
    sm: '8px',
    md: '12px',
    lg: '16px',
    xl: '24px',
    xxl: '32px',
    xxxl: '40px',
    huge: '48px'
};

/**
 * SPECIALIST MAPPING DATABASE
 */
const SPECIALIST_DATABASE = {
    head_neck: [
        { title: 'Neurologist', specialty: 'Brain & Nervous System Disorders', icon: '🧠' },
        { title: 'ENT Specialist', specialty: 'Ear, Nose, Throat Disorders', icon: '👂' },
        { title: 'General Practitioner', specialty: 'Initial Assessment & Referral', icon: '⚕️' }
    ],
    chest_lungs: [
        { title: 'Pulmonologist', specialty: 'Lung & Respiratory Diseases', icon: '🫁' },
        { title: 'Cardiologist', specialty: 'Heart & Cardiovascular Diseases', icon: '❤️' },
        { title: 'General Practitioner', specialty: 'Initial Assessment & Referral', icon: '⚕️' }
    ],
    abdomen_digestive: [
        { title: 'Gastroenterologist', specialty: 'Digestive System Disorders', icon: '🔬' },
        { title: 'General Practitioner', specialty: 'Initial Assessment & Referral', icon: '⚕️' },
        { title: 'Hepatologist', specialty: 'Liver & Pancreatic Diseases', icon: '🏥' }
    ],
    limbs_joints: [
        { title: 'Orthopedic Surgeon', specialty: 'Bone & Joint Disorders', icon: '🦴' },
        { title: 'Rheumatologist', specialty: 'Inflammatory Joint Diseases', icon: '🔬' },
        { title: 'General Practitioner', specialty: 'Initial Assessment & Referral', icon: '⚕️' }
    ],
    skin_surface: [
        { title: 'Dermatologist', specialty: 'Skin Diseases & Conditions', icon: '🩹' },
        { title: 'General Practitioner', specialty: 'Initial Assessment & Referral', icon: '⚕️' },
        { title: 'Allergist', specialty: 'Allergic Skin Reactions', icon: '🧬' }
    ],
    general_systemic: [
        { title: 'General Practitioner', specialty: 'Initial Assessment & Diagnosis', icon: '⚕️' },
        { title: 'Internal Medicine Specialist', specialty: 'Systemic Diseases', icon: '🏥' },
        { title: 'Infectious Disease Specialist', specialty: 'Infections & Fever', icon: '🦠' }
    ]
};

/**
 * DISEASE DESCRIPTION & TREATMENT DATABASE
 */
const DISEASE_DATABASE = {
    'Migraine': {
        description: 'Intense, often one-sided headache with throbbing pain, accompanied by nausea and sensitivity to light and sound.',
        causes: ['Stress and tension', 'Hormonal changes', 'Sleep deprivation', 'Dietary triggers', 'Environmental factors'],
        treatments: ['Triptan medications', 'Pain relievers', 'Preventive medications', 'Lifestyle modifications', 'Stress management'],
        warning_signs: ['Sudden severe headache', 'Vision changes', 'Difficulty speaking', 'Persistent numbness'],
        bodyPart: 'head_neck',
        severity: 'Moderate'
    },
    'Tension Headache': {
        description: 'Dull, pressure-like pain across the head, typically caused by muscle tension and stress.',
        causes: ['Stress and anxiety', 'Poor posture', 'Muscle tension', 'Caffeine withdrawal', 'Sleep disorders'],
        treatments: ['Relaxation techniques', 'Physical therapy', 'Pain relievers', 'Stress reduction', 'Neck exercises'],
        warning_signs: ['Persistent daily headache', 'Vision changes', 'Neurological symptoms'],
        bodyPart: 'head_neck',
        severity: 'Mild'
    },
    'Common Cold': {
        description: 'Viral upper respiratory infection causing runny nose, sneezing, sore throat, and mild fever.',
        causes: ['Viral infection (Rhinovirus)', 'Direct contact', 'Airborne transmission', 'Weakened immunity'],
        treatments: ['Rest and fluids', 'Symptomatic relief', 'Warm beverages', 'Honey for cough', 'Recovery in 7-10 days'],
        warning_signs: ['High fever (>103°F)', 'Difficulty breathing', 'Chest pain', 'Persistent cough >2 weeks'],
        bodyPart: 'head_neck',
        severity: 'Mild'
    },
    'Asthma': {
        description: 'Chronic respiratory condition causing airway inflammation, difficulty breathing, and wheezing.',
        causes: ['Genetic predisposition', 'Allergies', 'Environmental triggers', 'Exercise', 'Air pollution'],
        treatments: ['Inhaler medications', 'Bronchodilators', 'Corticosteroids', 'Trigger avoidance', 'Allergy management'],
        warning_signs: ['Severe difficulty breathing', 'Chest pain', 'Lips/fingertips turning blue', 'Confusion or difficulty speaking'],
        bodyPart: 'chest_lungs',
        severity: 'Moderate'
    },
    'Pneumonia': {
        description: 'Serious lung infection causing inflammation, productive cough, high fever, and difficulty breathing.',
        causes: ['Bacterial infection (Streptococcus pneumoniae)', 'Viral infection', 'Aspiration', 'Weakened immunity'],
        treatments: ['Antibiotics (for bacterial)', 'Supportive care', 'Oxygen therapy if needed', 'Fluids and rest'],
        warning_signs: ['Severe difficulty breathing', 'Chest pain', 'High fever', 'Confusion', 'Bluish lips'],
        bodyPart: 'chest_lungs',
        severity: 'Severe'
    },
    'Gastroenteritis': {
        description: 'Stomach and intestinal inflammation causing vomiting, diarrhea, cramping, and fever.',
        causes: ['Viral infection', 'Bacterial contamination', 'Food poisoning', 'Parasites', 'Contaminated water'],
        treatments: ['Hydration and electrolytes', 'Rest', 'Bland diet', 'Anti-nausea medication', 'Antibiotics if bacterial'],
        warning_signs: ['Severe dehydration', 'Blood in stool', 'Severe abdominal pain', 'High fever >102°F'],
        bodyPart: 'abdomen_digestive',
        severity: 'Moderate'
    },
    'Arthritis': {
        description: 'Joint inflammation causing chronic pain, stiffness, swelling, and reduced mobility.',
        causes: ['Osteoarthritis (wear and tear)', 'Rheumatoid arthritis (autoimmune)', 'Age', 'Genetics', 'Previous injury'],
        treatments: ['Pain relievers', 'Anti-inflammatory drugs', 'Physical therapy', 'Joint injections', 'Lifestyle modifications'],
        warning_signs: ['Severe joint pain', 'Inability to move joint', 'Deformity', 'Systemic symptoms'],
        bodyPart: 'limbs_joints',
        severity: 'Moderate'
    },
    'Eczema': {
        description: 'Chronic inflammatory skin condition causing extreme dryness, intense itching, and scaly red patches.',
        causes: ['Genetics', 'Weak skin barrier', 'Irritants', 'Allergens', 'Stress and hormones'],
        treatments: ['Moisturizers and emollients', 'Topical corticosteroids', 'Antihistamines', 'Trigger avoidance', 'Gentle skincare'],
        warning_signs: ['Signs of infection (pus, increased warmth)', 'Spreading rash', 'Severe itching disrupting sleep'],
        bodyPart: 'skin_surface',
        severity: 'Mild'
    },
    'Flu (Influenza)': {
        description: 'Viral infection causing high fever, body aches, chills, severe fatigue, and respiratory symptoms.',
        causes: ['Influenza virus A or B', 'Airborne transmission', 'Direct contact', 'Contaminated surfaces'],
        treatments: ['Antiviral medications (Tamiflu)', 'Rest', 'Fluids', 'Fever management', 'Supportive care'],
        warning_signs: ['Difficulty breathing', 'Chest pain', 'Confusion', 'Severe weakness', 'High fever >103°F'],
        bodyPart: 'general_systemic',
        severity: 'Moderate'
    }
};

/**
 * ANSWER-TO-DISEASE MAPPING
 * Maps specific symptoms/answers to diseases
 */
const ANSWER_DISEASE_MAPPING = {
    'persistent_headache': ['Migraine', 'Tension Headache'],
    'throbbing_pain': ['Migraine'],
    'pressure_sensation': ['Tension Headache'],
    'high_fever': ['Flu (Influenza)', 'Pneumonia'],
    'cough': ['Common Cold', 'Asthma', 'Pneumonia', 'Flu (Influenza)'],
    'sore_throat': ['Common Cold', 'Flu (Influenza)'],
    'runny_nose': ['Common Cold'],
    'difficulty_breathing': ['Asthma', 'Pneumonia', 'Flu (Influenza)'],
    'chest_pain': ['Pneumonia', 'Asthma'],
    'wheezing': ['Asthma'],
    'stomach_pain': ['Gastroenteritis'],
    'nausea': ['Gastroenteritis', 'Migraine', 'Flu (Influenza)'],
    'vomiting': ['Gastroenteritis', 'Migraine'],
    'diarrhea': ['Gastroenteritis'],
    'joint_pain': ['Arthritis'],
    'swelling': ['Arthritis'],
    'skin_irritation': ['Eczema'],
    'itching': ['Eczema'],
    'rash': ['Eczema']
};

/**
 * ICON COMPONENTS
 */
const Icons = {
    Brain: (props) => (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" />
            <circle cx="12" cy="8" r="1" fill="currentColor" />
            <circle cx="8" cy="12" r="1" fill="currentColor" />
            <circle cx="16" cy="12" r="1" fill="currentColor" />
            <circle cx="8" cy="16" r="1" fill="currentColor" />
            <circle cx="16" cy="16" r="1" fill="currentColor" />
        </svg>
    ),
    Lungs: (props) => (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
            <path d="M9 3v6c0 1.1-.9 2-2 2H5c-1.1 0-2 .9-2 2v5c0 1.1.9 2 2 2h2M15 3v6c0 1.1 .9 2 2 2h2c1.1 0 2 .9 2 2v5c0 1.1-.9 2-2 2h-2" />
            <line x1="12" y1="9" x2="12" y2="20" />
        </svg>
    ),
    Stomach: (props) => (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
            <path d="M12 2C8.13 2 5 4.13 5 7v10c0 2.87 3.13 5 7 5s7-2.13 7-5V7c0-2.87-3.13-5-7-5z" />
            <path d="M8 9h8M8 13h8" />
        </svg>
    ),
    Bones: (props) => (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
            <circle cx="5" cy="5" r="2" />
            <circle cx="19" cy="5" r="2" />
            <circle cx="5" cy="19" r="2" />
            <circle cx="19" cy="19" r="2" />
            <line x1="7" y1="5" x2="17" y2="5" />
            <line x1="7" y1="19" x2="17" y2="19" />
            <line x1="5" y1="7" x2="5" y2="17" />
            <line x1="19" y1="7" x2="19" y2="17" />
        </svg>
    ),
    Skin: (props) => (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
            <circle cx="8" cy="7" r="1.5" fill="currentColor" />
            <circle cx="16" cy="7" r="1.5" fill="currentColor" />
            <circle cx="12" cy="13" r="1.5" fill="currentColor" />
            <circle cx="5" cy="18" r="1.5" fill="currentColor" />
            <circle cx="19" cy="18" r="1.5" fill="currentColor" />
            <path d="M6 11c1.1 0 2 .9 2 2M16 11c-1.1 0-2 .9-2 2" />
        </svg>
    ),
    Heart: (props) => (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
        </svg>
    ),
    CheckCircle: (props) => (
        <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" {...props}>
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
        </svg>
    ),
    AlertTriangle: (props) => (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3.05h16.94a2 2 0 0 0 1.71-3.05L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
            <line x1="12" y1="9" x2="12" y2="13"></line>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
        </svg>
    ),
    Check: (props) => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" {...props}>
            <polyline points="20 6 9 17 4 12"></polyline>
        </svg>
    ),
    TrendingUp: (props) => (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
            <polyline points="23 6 13.5 15.5 8.5 10.5 1 17"></polyline>
            <polyline points="17 6 23 6 23 12"></polyline>
        </svg>
    ),
    BookOpen: (props) => (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
            <path d="M4 19.5h16M6 4h12v11H6z" />
            <path d="M6 15H4c-1 0-1 .5-1 1.5v2c0 1 0 1.5 1 1.5h2" />
            <path d="M18 15h2c1 0 1 .5 1 1.5v2c0 1 0 1.5-1 1.5h-2" />
        </svg>
    ),
};

/**
 * ERROR BOUNDARY
 */
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        console.error('Health Check Error:', error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            return (
                <div style={{
                    padding: SPACING.xxxl,
                    textAlign: 'center',
                    color: THEME.secondary
                }}>
                    <div style={{ fontSize: '48px', marginBottom: SPACING.lg }}>
                        <Icons.AlertTriangle style={{ color: THEME.danger }} />
                    </div>
                    <h3>Something went wrong</h3>
                    <p style={{ color: THEME.muted, marginBottom: SPACING.lg }}>
                        {this.state.error?.message || 'An unexpected error occurred'}
                    </p>
                </div>
            );
        }
        return this.props.children;
    }
}

/**
 * UTILITY FUNCTION - Extract diseases from answers
 */
const extractDiseasesFromAnswers = (answers) => {
    const diseaseSet = new Set();

    if (!answers || typeof answers !== 'object') return [];

    Object.values(answers).forEach(answerValue => {
        if (typeof answerValue === 'string') {
            const mapped = ANSWER_DISEASE_MAPPING[answerValue.toLowerCase()] || [];
            mapped.forEach(disease => diseaseSet.add(disease));
        }
    });

    return Array.from(diseaseSet);
};

/**
 * MAIN HEALTH PREDICTION SYSTEM
 */
const HealthCheck = ({ show, onHide, system }) => {
    // STATE
    const [view, setView] = useState('intro');
    const [bodyPartData, setBodyPartData] = useState(null);
    const [currentQIndex, setCurrentQIndex] = useState(0);
    const [multiSelectBuffer, setMultiSelectBuffer] = useState([]);
    const [analysisProgress, setAnalysisProgress] = useState(0);
    const [finalReport, setFinalReport] = useState(null);
    const [completedQuestions, setCompletedQuestions] = useState(0);
    const [selectedBodyPartId, setSelectedBodyPartId] = useState(null);
    const [isSystemValid, setIsSystemValid] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [predictedDiseases, setPredictedDiseases] = useState([]);
    const [userAnswers, setUserAnswers] = useState({});

    const scrollContainerRef = useRef(null);

    // INITIALIZATION
    useEffect(() => {
        if (show) {
            try {
                const isValid = system && typeof system.methods?.initializeSession === 'function';
                setIsSystemValid(isValid);

                if (!isValid) {
                    setErrorMessage('Health system data is not properly configured.');
                    return;
                }

                system.methods.initializeSession(`user_${Date.now()}`);
                resetState();
                setErrorMessage(null);
            } catch (error) {
                console.error('Initialization error:', error);
                setErrorMessage('Failed to initialize health check system');
                setIsSystemValid(false);
            }
        }
    }, [show, system]);

    useEffect(() => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = 0;
        }
    }, [currentQIndex]);

    // HELPERS
    const resetState = () => {
        setView('intro');
        setCurrentQIndex(0);
        setMultiSelectBuffer([]);
        setAnalysisProgress(0);
        setFinalReport(null);
        setCompletedQuestions(0);
        setSelectedBodyPartId(null);
        setBodyPartData(null);
        setIsProcessing(false);
        setPredictedDiseases([]);
        setUserAnswers({});
    };

    const handleBodyPartSelect = (partId) => {
        if (isProcessing) return;

        try {
            setIsProcessing(true);

            if (!system) {
                throw new Error('System object not available');
            }

            let data = null;

            if (typeof system.methods?.selectBodyPart === 'function') {
                try {
                    data = system.methods.selectBodyPart(partId);
                } catch (methodError) {
                    console.warn('selectBodyPart method failed:', methodError);
                }
            }

            if (!data) {
                if (system.symptoms && system.symptoms[partId]) {
                    data = {
                        questions: system.symptoms[partId].questions || [],
                        bodyPartLabel: system.bodyParts?.[partId]?.label || 'Assessment'
                    };
                } else if (system.bodyParts && system.bodyParts[partId]) {
                    const bodyPart = system.bodyParts[partId];
                    data = {
                        questions: bodyPart.symptoms || bodyPart.questions || [],
                        bodyPartLabel: bodyPart.label || 'Assessment'
                    };
                }
            }

            if (!data?.questions || !Array.isArray(data.questions) || data.questions.length === 0) {
                setErrorMessage(`No questions available for ${system.bodyParts?.[partId]?.label || 'this body part'}`);
                setIsProcessing(false);
                return;
            }

            const validQuestions = data.questions.filter(q =>
                q && q.id && q.text && Array.isArray(q.options) && q.options.length > 0
            );

            if (validQuestions.length === 0) {
                setErrorMessage('Questions are not properly formatted.');
                setIsProcessing(false);
                return;
            }

            setTimeout(() => {
                setSelectedBodyPartId(partId);
                setBodyPartData({
                    ...data,
                    questions: validQuestions
                });
                setView('questions');
                setCurrentQIndex(0);
                setCompletedQuestions(0);
                setMultiSelectBuffer([]);
                setUserAnswers({});
                setErrorMessage(null);
                setIsProcessing(false);
            }, 100);
        } catch (error) {
            console.error('Error selecting body part:', error);
            setErrorMessage(`Failed to load body part data: ${error.message}`);
            setIsProcessing(false);
        }
    };

    const handleOptionSelect = (question, optionValue) => {
        if (isProcessing) return;

        try {
            if (!question || !optionValue) return;

            // Track user answers
            setUserAnswers(prev => ({
                ...prev,
                [question.id]: optionValue
            }));

            if (question.type === 'multiple_select') {
                setMultiSelectBuffer(prev =>
                    prev.includes(optionValue)
                        ? prev.filter(v => v !== optionValue)
                        : [...prev, optionValue]
                );
            } else {
                setIsProcessing(true);

                if (typeof system.methods?.recordAnswer === 'function') {
                    try {
                        system.methods.recordAnswer(question.id, optionValue);
                    } catch (recordError) {
                        console.warn('recordAnswer method failed:', recordError);
                    }
                }

                setCompletedQuestions(prev => prev + 1);

                setTimeout(() => {
                    nextQuestion();
                    setIsProcessing(false);
                }, 300);
            }
        } catch (error) {
            console.error('Error selecting option:', error);
            setErrorMessage('Failed to record your answer');
            setIsProcessing(false);
        }
    };

    const confirmMultiSelect = (questionId) => {
        if (isProcessing || multiSelectBuffer.length === 0) return;

        try {
            setIsProcessing(true);

            // Track user answers for multi-select
            setUserAnswers(prev => ({
                ...prev,
                [questionId]: multiSelectBuffer
            }));

            if (typeof system.methods?.recordAnswer === 'function') {
                try {
                    multiSelectBuffer.forEach(val => {
                        system.methods.recordAnswer(questionId, val);
                    });
                } catch (recordError) {
                    console.warn('recordAnswer method failed:', recordError);
                }
            }

            setCompletedQuestions(prev => prev + 1);
            setMultiSelectBuffer([]);
            setErrorMessage(null);

            setTimeout(() => {
                nextQuestion();
                setIsProcessing(false);
            }, 300);
        } catch (error) {
            console.error('Error confirming multi-select:', error);
            setErrorMessage('Failed to process your selections');
            setIsProcessing(false);
        }
    };

    const nextQuestion = () => {
        if (!bodyPartData?.questions) return;

        if (currentQIndex < bodyPartData.questions.length - 1) {
            setCurrentQIndex(prev => prev + 1);
        } else {
            finishAssessment();
        }
    };

    const previousQuestion = () => {
        if (currentQIndex > 0) {
            setCurrentQIndex(prev => prev - 1);
        }
    };

    const finishAssessment = () => {
        setView('analyzing');
        let progress = 0;

        const interval = setInterval(() => {
            progress += Math.floor(Math.random() * 18) + 5;

            if (progress >= 100) {
                progress = 100;
                clearInterval(interval);

                try {
                    system.methods.calculateSymptomScore?.();
                    system.methods.predictDiseases?.();
                    system.methods.generateAnalysis?.();
                    system.methods.generateRecommendations?.();

                    // ✅ GET DATA
                    const rawReport = system.methods.getFullReport?.();
                    const allPredictions = rawReport?.predictions || [];
                    const answerBasedDiseases = extractDiseasesFromAnswers(userAnswers);

                    // ✅ APPLY USER REQUIREMENTS:
                    // 1. If confidence is 0% -> Default to 60%
                    // 2. If disease is "Unknown" -> Use the first disease from the Disease Blog (answer-based)
                    const refinedPredictions = (allPredictions.length > 0 ? allPredictions : []).map(pred => {
                        let disease = pred.disease;
                        let confidence = pred.confidence;

                        if (confidence === 0 || !confidence) {
                            confidence = 60;
                        }

                        if (!disease || disease.toLowerCase() === 'unknown') {
                            disease = answerBasedDiseases[0] || 'Assessment Result';
                        }

                        return { ...pred, disease, confidence };
                    });

                    // If system returned nothing but we have blog matches, use them as primary predictions
                    let finalPredictions = refinedPredictions;
                    if (finalPredictions.length === 0 && answerBasedDiseases.length > 0) {
                        finalPredictions = answerBasedDiseases.map(dName => ({
                            disease: dName,
                            confidence: 60
                        }));
                    }

                    setPredictedDiseases(finalPredictions);

                    let report = rawReport;

                    if (!report) {
                        report = {
                            predictions: finalPredictions,
                            analysis: {
                                symptomSeverity: 'Moderate',
                                overallRisk: 'Medium'
                            },
                            recommendations: {
                                primary_care: ['Consult a healthcare provider', 'Describe all symptoms clearly'],
                                specialist: ['Based on symptoms, a specialist may be recommended'],
                                self_care: ['Rest adequately', 'Stay hydrated', 'Monitor symptoms']
                            }
                        };
                    } else {
                        report = { ...report, predictions: finalPredictions };
                    }

                    setFinalReport(report);
                    setErrorMessage(null);

                    setTimeout(() => {
                        setView('results');
                    }, 600);
                } catch (error) {
                    console.error('Error during analysis:', error);

                    const answerBased = extractDiseasesFromAnswers(userAnswers);
                    const fallbackReport = {
                        predictions: answerBased.map(dName => ({ disease: dName, confidence: 60 })),
                        analysis: {
                            symptomSeverity: 'Recorded',
                            overallRisk: 'Pending'
                        },
                        recommendations: {
                            primary_care: ['Consult a healthcare provider'],
                            specialist: ['Professional evaluation recommended'],
                            self_care: ['Monitor your symptoms closely']
                        }
                    };

                    setFinalReport(fallbackReport);
                    setTimeout(() => {
                        setView('results');
                    }, 600);
                }
            }

            setAnalysisProgress(progress);
        }, 200);
    };

    const handleDownloadReport = () => {
        if (!finalReport) return;

        const { analysis = {}, predictions = [], recommendations = {} } = finalReport;
        const answerBasedDiseases = extractDiseasesFromAnswers(userAnswers);

        let reportTxt = `====================================================
MEDICAL ASSESSMENT REPORT - SMART HEALTH PREDICTOR
Generated on: ${new Date().toLocaleString()}
====================================================\n\n`;

        reportTxt += `OVERALL ASSESSMENT:\n`;
        reportTxt += `- Severity: ${analysis.symptomSeverity || 'Moderate'}\n`;
        reportTxt += `- Risk Level: ${analysis.overallRisk || 'Medium'}\n\n`;

        reportTxt += `PRIMARY DISEASE PREDICTIONS:\n`;
        predictions.forEach((pred, idx) => {
            reportTxt += `${idx + 1}. ${pred.disease} (${Math.round(pred.confidence)}% Confidence)\n`;
            const info = DISEASE_DATABASE[pred.disease];
            if (info) {
                reportTxt += `   Description: ${info.description}\n`;
                reportTxt += `   Treatments: ${info.treatments?.join(', ')}\n`;
            }
            reportTxt += `\n`;
        });

        reportTxt += `DISEASE BLOG (SYMPTOM-BASED INSIGHTS):\n`;
        Object.entries(userAnswers).forEach(([qid, val]) => {
            const diseases = Array.isArray(val)
                ? val.flatMap(v => ANSWER_DISEASE_MAPPING[v.toLowerCase()] || [])
                : (ANSWER_DISEASE_MAPPING[val.toLowerCase()] || []);

            if (diseases.length > 0) {
                reportTxt += `- For Symptoms [${Array.isArray(val) ? val.join(', ') : val}]:\n`;
                const uniqueDiseases = [...new Set(diseases)];
                uniqueDiseases.forEach(d => reportTxt += `  * ${d}\n`);
            }
        });
        reportTxt += `\n`;

        reportTxt += `RECOMMENDED SPECIALISTS:\n`;
        const specialists = SPECIALIST_DATABASE[selectedBodyPartId] || SPECIALIST_DATABASE.general_systemic;
        specialists.forEach(s => {
            reportTxt += `- ${s.title}: ${s.specialty}\n`;
        });
        reportTxt += `\n`;

        reportTxt += `MEDICAL CARE ADVICE:\n`;
        if (recommendations.primary_care) {
            recommendations.primary_care.forEach(r => reportTxt += `- ${r}\n`);
        }
        reportTxt += `\n`;

        reportTxt += `====================================================
IMPORTANT DISCLAIMER:
This report is AI-generated for informational purposes only.
It DOES NOT replace professional medical diagnosis or treatment.
Please consult a qualified healthcare provider immediately.
====================================================`;

        const blob = new Blob([reportTxt], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Medical_Report_${Date.now()}.txt`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const startNewAssessment = () => {
        try {
            system.methods.resetSession?.();
            resetState();
        } catch (error) {
            console.error('Error resetting:', error);
        }
    };

    // RENDER FUNCTIONS
    const renderIntroScreen = () => (
        <div style={{
            background: `linear-gradient(135deg, ${THEME.primarySofter} 0%, ${THEME.white} 100%)`,
            animation: 'fadeIn 0.6s ease-out',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            padding: SPACING.huge
        }}>
            <style>{`
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>

            <div style={{
                width: '100px',
                height: '100px',
                background: `linear-gradient(135deg, ${THEME.primary}, ${THEME.primaryLight})`,
                borderRadius: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: SPACING.xxl,
                boxShadow: THEME.shadowLg,
                color: 'white'
            }}>
                <Icons.Heart style={{ width: '50px', height: '50px' }} />
            </div>

            <h1 style={{
                fontSize: '32px',
                fontWeight: '700',
                color: THEME.secondary,
                marginBottom: SPACING.lg,
                textAlign: 'center'
            }}>Smart Health Predictor</h1>

            <p style={{
                fontSize: '16px',
                color: THEME.muted,
                textAlign: 'center',
                marginBottom: SPACING.xxxl,
                lineHeight: '1.6',
                maxWidth: '400px'
            }}>
                AI-powered symptom analysis with disease prediction and specialist recommendations
            </p>

            <ul style={{
                listStyle: 'none',
                padding: 0,
                marginBottom: SPACING.xxxl,
                width: '100%'
            }}>
                {[
                    'Intelligent Disease Prediction System',
                    'Complete Disease List Display',
                    'Disease Blog with Answer Mapping',
                    'Recommended Health Specialists',
                    'Personalized Medical Report'
                ].map((feature, idx) => (
                    <li key={idx} style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: SPACING.lg,
                        fontSize: '15px',
                        color: THEME.tertiary
                    }}>
                        <div style={{
                            width: '32px',
                            height: '32px',
                            background: THEME.primarySoft,
                            color: THEME.primary,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginRight: SPACING.md,
                            flexShrink: 0,
                            fontWeight: 'bold'
                        }}>
                            <Icons.Check />
                        </div>
                        {feature}
                    </li>
                ))}
            </ul>

            <button
                onClick={() => setView('bodypart')}
                style={{
                    background: `linear-gradient(135deg, ${THEME.primary}, ${THEME.primaryLight})`,
                    color: 'white',
                    border: 'none',
                    padding: `${SPACING.lg} ${SPACING.xl}`,
                    fontSize: '16px',
                    fontWeight: '600',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    width: '100%',
                    boxShadow: THEME.shadowMd,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
                onMouseOver={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = THEME.shadowLg;
                }}
                onMouseOut={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = THEME.shadowMd;
                }}
            >
                Start Health Prediction
            </button>

            <p style={{
                fontSize: '12px',
                color: THEME.mutedLight,
                textAlign: 'center',
                marginTop: SPACING.xxl,
                lineHeight: '1.6'
            }}>
                ⚠️ Medical Disclaimer: This tool provides predictions for informational purposes only and does not replace professional medical diagnosis.
            </p>
        </div>
    );

    const renderBodyPartSelection = () => {
        const bodyParts = system?.bodyParts ? Object.values(system.bodyParts).filter(part => part && part.id && part.label) : [];

        if (bodyParts.length === 0) {
            return (
                <div style={{ textAlign: 'center', padding: SPACING.xxxl, color: THEME.secondary }}>
                    <Icons.AlertTriangle style={{ color: THEME.warning, marginBottom: SPACING.lg }} />
                    <h3>No Body Parts Available</h3>
                </div>
            );
        }

        const iconMap = {
            head_neck: Icons.Brain,
            chest_lungs: Icons.Lungs,
            abdomen_digestive: Icons.Stomach,
            limbs_joints: Icons.Bones,
            skin_surface: Icons.Skin,
            general_systemic: Icons.Heart
        };

        return (
            <div style={{ animation: 'fadeIn 0.5s ease-out' }}>
                <div style={{ textAlign: 'center', marginBottom: SPACING.xxxl }}>
                    <h2 style={{
                        fontSize: '28px',
                        fontWeight: '700',
                        color: THEME.secondary,
                        marginBottom: SPACING.md
                    }}>Select Affected Area</h2>
                    <p style={{
                        fontSize: '15px',
                        color: THEME.muted,
                        lineHeight: '1.5'
                    }}>Choose the body region where you're experiencing symptoms</p>
                </div>

                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                    gap: SPACING.lg,
                    marginBottom: SPACING.xxl
                }}>
                    {bodyParts.map((part) => {
                        const IconComponent = iconMap[part.id] || Icons.Heart;
                        return (
                            <div
                                key={part.id}
                                onClick={() => !isProcessing && handleBodyPartSelect(part.id)}
                                onKeyPress={(e) => {
                                    if ((e.key === 'Enter' || e.key === ' ') && !isProcessing) {
                                        handleBodyPartSelect(part.id);
                                    }
                                }}
                                role="button"
                                tabIndex={0}
                                style={{
                                    background: THEME.white,
                                    border: `2px solid ${THEME.border}`,
                                    borderRadius: '16px',
                                    padding: SPACING.xxl,
                                    cursor: isProcessing ? 'not-allowed' : 'pointer',
                                    textAlign: 'center',
                                    opacity: isProcessing ? 0.6 : 1,
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    position: 'relative',
                                    overflow: 'hidden'
                                }}
                                onMouseOver={(e) => {
                                    if (!isProcessing) {
                                        e.currentTarget.style.borderColor = THEME.primary;
                                        e.currentTarget.style.background = THEME.primarySofter;
                                        e.currentTarget.style.transform = 'translateY(-4px)';
                                        e.currentTarget.style.boxShadow = THEME.shadowMd;
                                    }
                                }}
                                onMouseOut={(e) => {
                                    e.currentTarget.style.borderColor = THEME.border;
                                    e.currentTarget.style.background = THEME.white;
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = 'none';
                                }}
                            >
                                <div style={{
                                    fontSize: '44px',
                                    marginBottom: SPACING.md,
                                    display: 'block',
                                    color: THEME.primary
                                }}>
                                    <IconComponent />
                                </div>
                                <div style={{
                                    fontSize: '15px',
                                    fontWeight: '600',
                                    color: THEME.secondary,
                                    marginBottom: SPACING.sm
                                }}>{part.label}</div>
                                <div style={{
                                    fontSize: '12px',
                                    color: THEME.muted,
                                    lineHeight: '1.4',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden'
                                }}>{part.description}</div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    const renderQuestionnaire = () => {
        if (!bodyPartData?.questions || bodyPartData.questions.length === 0) {
            return (
                <div style={{ textAlign: 'center', padding: SPACING.xxxl, color: THEME.secondary }}>
                    <Icons.AlertTriangle style={{ color: THEME.danger, marginBottom: SPACING.lg }} />
                    <h3>No Questions Available</h3>
                </div>
            );
        }

        const question = bodyPartData.questions[currentQIndex];
        const isMulti = question?.type === 'multiple_select';
        const progress = ((currentQIndex + completedQuestions) / (bodyPartData.questions.length * 2)) * 100;

        if (!question) {
            return <div style={{ textAlign: 'center', padding: SPACING.xxxl }}>Loading...</div>;
        }

        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                animation: 'slideUp 0.4s ease-out'
            }}>
                <div style={{ marginBottom: SPACING.xxl }}>
                    <div style={{
                        width: '100%',
                        height: '6px',
                        background: THEME.borderLight,
                        borderRadius: '10px',
                        overflow: 'hidden',
                        marginBottom: SPACING.md
                    }}>
                        <div
                            style={{
                                height: '100%',
                                background: `linear-gradient(90deg, ${THEME.primary}, ${THEME.primaryLight})`,
                                borderRadius: '10px',
                                width: `${Math.min(progress, 100)}%`,
                                transition: 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                                boxShadow: '0 0 10px rgba(16, 185, 129, 0.3)'
                            }}
                        />
                    </div>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        fontSize: '12px',
                        fontWeight: '600'
                    }}>
                        <span style={{ color: THEME.muted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Question {currentQIndex + 1} of {bodyPartData.questions.length}
                        </span>
                        <span style={{
                            background: THEME.primarySoft,
                            color: THEME.primaryDark,
                            padding: `${SPACING.xs} ${SPACING.md}`,
                            borderRadius: '20px',
                            fontSize: '11px',
                            fontWeight: '700'
                        }}>
                            {bodyPartData.bodyPartLabel || 'Assessment'}
                        </span>
                    </div>
                </div>

                <div
                    ref={scrollContainerRef}
                    style={{
                        flex: 1,
                        overflowY: 'auto',
                        paddingRight: SPACING.sm,
                        marginBottom: SPACING.xxl
                    }}
                >
                    <div style={{ marginBottom: SPACING.xxxl }}>
                        <h3 style={{
                            fontSize: '22px',
                            fontWeight: '700',
                            color: THEME.secondary,
                            lineHeight: '1.4',
                            marginBottom: SPACING.md
                        }}>{question.text}</h3>
                        <p style={{
                            fontSize: '14px',
                            color: THEME.muted,
                            marginBottom: SPACING.xxl,
                            fontStyle: 'italic'
                        }}>
                            {isMulti
                                ? 'Select all options that apply to your symptoms'
                                : 'Choose the option that best describes your condition'}
                        </p>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: SPACING.md }}>
                            {Array.isArray(question.options) && question.options.map((opt, idx) => {
                                if (!opt?.value || !opt?.label) return null;

                                const isSelected = isMulti
                                    ? multiSelectBuffer.includes(opt.value)
                                    : false;

                                return (
                                    <button
                                        key={idx}
                                        onClick={() => handleOptionSelect(question, opt.value)}
                                        disabled={isProcessing}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            width: '100%',
                                            padding: `${SPACING.lg} ${SPACING.xl}`,
                                            background: isSelected ? THEME.primarySoft : THEME.white,
                                            border: `2px solid ${isSelected ? THEME.primary : THEME.border}`,
                                            borderRadius: '12px',
                                            cursor: isProcessing ? 'not-allowed' : 'pointer',
                                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                            fontSize: '15px',
                                            fontWeight: isSelected ? '600' : '500',
                                            color: THEME.secondary,
                                            textAlign: 'left',
                                            opacity: isProcessing ? 0.6 : 1
                                        }}
                                        onMouseOver={(e) => {
                                            if (!isProcessing) {
                                                e.target.style.borderColor = THEME.primary;
                                                e.target.style.background = THEME.primarySofter;
                                                e.target.style.transform = 'translateX(4px)';
                                            }
                                        }}
                                        onMouseOut={(e) => {
                                            if (!isSelected) {
                                                e.target.style.borderColor = THEME.border;
                                                e.target.style.background = THEME.white;
                                            }
                                            e.target.style.transform = 'translateX(0)';
                                        }}
                                    >
                                        {isMulti && (
                                            <div style={{
                                                width: '22px',
                                                height: '22px',
                                                border: `2px solid ${isSelected ? THEME.primary : THEME.border}`,
                                                borderRadius: '6px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                marginRight: SPACING.lg,
                                                flexShrink: 0,
                                                background: isSelected ? THEME.primary : THEME.white,
                                                color: 'white',
                                                transition: 'all 0.2s'
                                            }}>
                                                {isSelected && <Icons.Check />}
                                            </div>
                                        )}
                                        <span>{opt.label}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>

                <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: SPACING.md,
                    paddingTop: SPACING.xxl,
                    borderTop: `1px solid ${THEME.border}`
                }}>
                    <button
                        onClick={previousQuestion}
                        disabled={currentQIndex === 0 || isProcessing}
                        style={{
                            padding: `${SPACING.md} ${SPACING.xl}`,
                            fontSize: '14px',
                            fontWeight: '600',
                            borderRadius: '10px',
                            background: THEME.backgroundLight,
                            color: THEME.secondary,
                            border: `1px solid ${THEME.border}`,
                            cursor: currentQIndex === 0 || isProcessing ? 'not-allowed' : 'pointer',
                            transition: 'all 0.3s',
                            opacity: currentQIndex === 0 || isProcessing ? 0.5 : 1
                        }}
                    >
                        ← Back
                    </button>

                    {isMulti ? (
                        <button
                            onClick={() => confirmMultiSelect(question.id)}
                            disabled={multiSelectBuffer.length === 0 || isProcessing}
                            style={{
                                padding: `${SPACING.md} ${SPACING.xl}`,
                                fontSize: '14px',
                                fontWeight: '600',
                                borderRadius: '10px',
                                background: `linear-gradient(135deg, ${THEME.primary}, ${THEME.primaryLight})`,
                                color: 'white',
                                border: 'none',
                                cursor: multiSelectBuffer.length === 0 || isProcessing ? 'not-allowed' : 'pointer',
                                boxShadow: THEME.shadowMd,
                                opacity: multiSelectBuffer.length === 0 || isProcessing ? 0.5 : 1,
                                transition: 'all 0.3s'
                            }}
                        >
                            Continue →
                        </button>
                    ) : (
                        <button
                            disabled={true}
                            style={{
                                padding: `${SPACING.md} ${SPACING.xl}`,
                                fontSize: '14px',
                                fontWeight: '600',
                                borderRadius: '10px',
                                background: `linear-gradient(135deg, ${THEME.primary}, ${THEME.primaryLight})`,
                                color: 'white',
                                border: 'none',
                                opacity: 0.5,
                                cursor: 'not-allowed'
                            }}
                        >
                            Auto-advancing...
                        </button>
                    )}
                </div>
            </div>
        );
    };

    const renderAnalyzingScreen = () => (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            background: `linear-gradient(135deg, ${THEME.primarySofter} 0%, ${THEME.white} 100%)`,
            animation: 'fadeIn 0.5s ease-out',
            padding: SPACING.xxl
        }}>
            <svg width="80" height="80" viewBox="0 0 100 100" style={{ marginBottom: SPACING.xxxl, filter: 'drop-shadow(0 2px 8px rgba(16, 185, 129, 0.2))' }}>
                <circle cx="50" cy="50" r="45" fill="none" stroke={THEME.primarySoft} strokeWidth="3" />
                <circle
                    cx="50" cy="50" r="45"
                    fill="none"
                    stroke={THEME.primary}
                    strokeWidth="3"
                    strokeDasharray="141"
                    strokeDashoffset={`${141 - (analysisProgress / 100) * 141}`}
                    strokeLinecap="round"
                    style={{ transition: 'stroke-dashoffset 0.3s ease-out' }}
                />
            </svg>

            <h2 style={{
                fontSize: '24px',
                fontWeight: '700',
                color: THEME.secondary,
                marginBottom: SPACING.md,
                textAlign: 'center'
            }}>Analyzing Your Symptoms</h2>

            <p style={{
                fontSize: '14px',
                color: THEME.muted,
                textAlign: 'center',
                marginBottom: SPACING.xxxl
            }}>Generating disease predictions and specialist recommendations...</p>

            <div style={{ width: '100%', maxWidth: '300px' }}>
                <div style={{
                    width: '100%',
                    height: '8px',
                    background: THEME.borderLight,
                    borderRadius: '10px',
                    overflow: 'hidden',
                    marginBottom: SPACING.md
                }}>
                    <div
                        style={{
                            height: '100%',
                            background: `linear-gradient(90deg, ${THEME.primary}, ${THEME.primaryLight})`,
                            borderRadius: '10px',
                            width: `${Math.min(analysisProgress, 100)}%`,
                            transition: 'width 0.3s ease-out',
                            boxShadow: '0 0 12px rgba(16, 185, 129, 0.4)'
                        }}
                    />
                </div>
                <div style={{
                    textAlign: 'center',
                    fontSize: '13px',
                    color: THEME.muted,
                    fontWeight: '600'
                }}>{analysisProgress}% Complete</div>
            </div>

            <div style={{ marginTop: SPACING.xxxl, display: 'flex', flexDirection: 'column', gap: SPACING.md }}>
                {[
                    { label: 'Analyzing symptoms', progress: true },
                    { label: 'Matching disease patterns', progress: true },
                    { label: 'Generating specialist recommendations', progress: analysisProgress > 66 }
                ].map((step, idx) => (
                    <div key={idx} style={{ display: 'flex', alignItems: 'center', fontSize: '13px', color: THEME.muted }}>
                        <div style={{
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            background: THEME.primarySoft,
                            color: THEME.primary,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginRight: SPACING.md,
                            fontWeight: 'bold',
                            fontSize: '11px'
                        }}>
                            {step.progress ? '✓' : '·'}
                        </div>
                        <span>{step.label}</span>
                    </div>
                ))}
            </div>
        </div>
    );

    const renderResults = () => {
        if (!finalReport) {
            return <div style={{ textAlign: 'center', padding: SPACING.xxxl }}>Loading results...</div>;
        }

        const { analysis = {}, predictions = [], recommendations = {} } = finalReport;
        const bodyPartId = selectedBodyPartId;
        const specialists = SPECIALIST_DATABASE[bodyPartId] || SPECIALIST_DATABASE.general_systemic;
        const answlen = Object.entries(userAnswers).length;
        return (
            <div style={{ animation: 'fadeIn 0.6s ease-out' }}>
                <div style={{
                    textAlign: 'center',
                    paddingBottom: SPACING.xxl,
                    marginBottom: SPACING.xxl,
                    borderBottom: `1px solid ${THEME.border}`
                }}>
                    <div style={{
                        width: '80px',
                        height: '80px',
                        background: `linear-gradient(135deg, ${THEME.primarySoft}, ${THEME.primarySofter})`,
                        borderRadius: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto ' + SPACING.lg,
                        color: THEME.primary
                    }}>
                        <Icons.CheckCircle />
                    </div>
                    <h2 style={{
                        fontSize: '24px',
                        fontWeight: '700',
                        color: THEME.secondary,
                        marginBottom: SPACING.sm
                    }}>Assessment Complete</h2>
                    <p style={{
                        fontSize: '14px',
                        color: THEME.muted
                    }}>
                        Disease predictions, disease blog, and specialist recommendations
                    </p>
                </div>

                <div style={{
                    marginBottom: SPACING.xxl
                }}>
                    {/* SEVERITY CARD */}
                    <div style={{ marginBottom: SPACING.xxl }}>
                        <div style={{
                            background: `linear-gradient(135deg, ${THEME.primarySofter}, ${THEME.primarySoft})`,
                            border: `2px solid ${THEME.primarySoft}`,
                            borderRadius: '16px',
                            padding: SPACING.xl,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}>
                            <div>
                                <div style={{
                                    fontSize: '12px',
                                    color: THEME.muted,
                                    textTransform: 'uppercase',
                                    fontWeight: '600',
                                    marginBottom: SPACING.xs
                                }}>Overall Severity</div>
                                <div style={{
                                    fontSize: '20px',
                                    fontWeight: '700',
                                    color: THEME.secondary
                                }}>{analysis.symptomSeverity || 'Moderate'}</div>
                            </div>
                            <div style={{
                                background: analysis.overallRisk === 'High' ? '#fee2e2' : analysis.overallRisk === 'Medium' ? '#fef3c7' : '#d1fae5',
                                color: analysis.overallRisk === 'High' ? '#dc2626' : analysis.overallRisk === 'Medium' ? '#d97706' : '#047857',
                                padding: `${SPACING.sm} ${SPACING.lg}`,
                                borderRadius: '20px',
                                fontSize: '12px',
                                fontWeight: '700',
                                textTransform: 'uppercase'
                            }}>
                                {analysis.overallRisk || 'Medium'} Risk
                            </div>
                        </div>
                    </div>

                    {/* PRIMARY DISEASE PREDICTIONS */}
                    {predictions && predictions.length > 0 && (
                        <div style={{ marginBottom: SPACING.xxl }}>
                            <div style={{
                                fontSize: '13px',
                                fontWeight: '700',
                                color: THEME.muted,
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                marginBottom: SPACING.md,
                                display: 'flex',
                                alignItems: 'center'
                            }}>
                                <span style={{
                                    display: 'inline-block',
                                    width: '8px',
                                    height: '8px',
                                    background: THEME.danger,
                                    borderRadius: '50%',
                                    marginRight: SPACING.sm
                                }}></span>
                                Primary Disease Predictions (Top 3)
                            </div>
                            {predictions.slice(0, 3).map((pred, idx) => {
                                const diseaseInfo = DISEASE_DATABASE[pred.disease];
                                return (
                                    <div key={idx} style={{
                                        background: THEME.white,
                                        border: `2px solid ${THEME.border}`,
                                        borderRadius: '16px',
                                        padding: SPACING.xl,
                                        marginBottom: SPACING.md,
                                        transition: 'all 0.3s',
                                        boxShadow: idx === 0 ? `0 0 0 3px ${THEME.primarySoft}` : 'none'
                                    }}>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            justifyContent: 'space-between',
                                            marginBottom: SPACING.md
                                        }}>
                                            <div>
                                                <div style={{
                                                    fontSize: '16px',
                                                    fontWeight: '700',
                                                    color: THEME.secondary,
                                                    marginBottom: SPACING.xs,
                                                    display: 'flex',
                                                    alignItems: 'center'
                                                }}>
                                                    {idx + 1}. {pred.disease || 'Unknown'}
                                                </div>
                                                <div style={{
                                                    fontSize: '12px',
                                                    color: THEME.muted
                                                }}>Prediction Confidence Level</div>
                                            </div>
                                            <div style={{
                                                fontSize: '24px',
                                                fontWeight: '700',
                                                color: THEME.primary,
                                                textAlign: 'right'
                                            }}>{Math.round(pred.confidence || 0)}%</div>
                                        </div>
                                        <div style={{
                                            height: '6px',
                                            background: THEME.borderLight,
                                            borderRadius: '10px',
                                            overflow: 'hidden',
                                            marginBottom: SPACING.md
                                        }}>
                                            <div
                                                style={{
                                                    height: '100%',
                                                    background: `linear-gradient(90deg, ${THEME.primary}, ${THEME.primaryLight})`,
                                                    borderRadius: '10px',
                                                    width: `${pred.confidence || 0}%`
                                                }}
                                            />
                                        </div>
                                        {diseaseInfo && (
                                            <div>
                                                <div style={{
                                                    fontSize: '13px',
                                                    color: THEME.muted,
                                                    lineHeight: '1.6',
                                                    marginBottom: SPACING.md,
                                                    padding: SPACING.md,
                                                    background: THEME.backgroundLighter,
                                                    borderRadius: '8px'
                                                }}>
                                                    <strong>Description:</strong> {diseaseInfo.description}
                                                </div>

                                                <div style={{ marginBottom: SPACING.md }}>
                                                    <div style={{
                                                        fontSize: '12px',
                                                        fontWeight: '600',
                                                        color: THEME.secondary,
                                                        marginBottom: SPACING.xs
                                                    }}>Common Causes:</div>
                                                    <div style={{
                                                        display: 'flex',
                                                        flexWrap: 'wrap',
                                                        gap: SPACING.xs
                                                    }}>
                                                        {diseaseInfo.causes && diseaseInfo.causes.slice(0, 3).map((cause, cidx) => (
                                                            <span key={cidx} style={{
                                                                display: 'inline-block',
                                                                fontSize: '11px',
                                                                background: THEME.primarySofter,
                                                                color: THEME.primary,
                                                                padding: `${SPACING.xs} ${SPACING.md}`,
                                                                borderRadius: '12px',
                                                                fontWeight: '500'
                                                            }}>
                                                                {cause}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>

                                                <div style={{ marginBottom: SPACING.md }}>
                                                    <div style={{
                                                        fontSize: '12px',
                                                        fontWeight: '600',
                                                        color: THEME.secondary,
                                                        marginBottom: SPACING.xs
                                                    }}>Treatment Options:</div>
                                                    <div style={{
                                                        fontSize: '12px',
                                                        color: THEME.muted,
                                                        lineHeight: '1.6',
                                                        padding: `${SPACING.sm} ${SPACING.md}`,
                                                        background: '#f0f9ff',
                                                        borderLeft: `3px solid ${THEME.info}`,
                                                        borderRadius: '4px'
                                                    }}>
                                                        {diseaseInfo.treatments && diseaseInfo.treatments.slice(0, 2).map((treatment, tidx) => (
                                                            <div key={tidx}>{tidx + 1}. {treatment}</div>
                                                        ))}
                                                    </div>
                                                </div>

                                                <div style={{
                                                    padding: `${SPACING.sm} ${SPACING.md}`,
                                                    background: '#fef2f2',
                                                    border: `1px solid ${THEME.danger}`,
                                                    borderRadius: '8px',
                                                    fontSize: '12px',
                                                    color: THEME.danger,
                                                    lineHeight: '1.5'
                                                }}>
                                                    <strong>⚠️ Warning Signs:</strong> {diseaseInfo.warning_signs && diseaseInfo.warning_signs.slice(0, 2).join(', ')}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* ✅ FULL DISEASE LIST - ALL PREDICTED DISEASES */}
                    {predictions && predictions.length > 0 && (
                        <div style={{ marginBottom: SPACING.xxl }}>
                            <div style={{
                                fontSize: '13px',
                                fontWeight: '700',
                                color: THEME.muted,
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                marginBottom: SPACING.md,
                                display: 'flex',
                                alignItems: 'center'
                            }}>
                                <span style={{
                                    display: 'inline-block',
                                    width: '8px',
                                    height: '8px',
                                    background: THEME.info,
                                    borderRadius: '50%',
                                    marginRight: SPACING.sm
                                }}></span>
                                All Predicted Diseases ({predictions.length} Total)
                            </div>
                            <div style={{
                                background: THEME.white,
                                border: `2px solid ${THEME.border}`,
                                borderRadius: '16px',
                                padding: SPACING.lg,
                                display: 'grid',
                                gap: SPACING.md
                            }}>
                                {predictions.map((pred, idx) => {
                                    const diseaseInfo = DISEASE_DATABASE[pred.disease];
                                    return (
                                        <div key={idx} style={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            padding: SPACING.md,
                                            background: idx % 2 === 0 ? THEME.backgroundLighter : THEME.white,
                                            borderRadius: '10px',
                                            border: `1px solid ${THEME.borderLight}`
                                        }}>
                                            <div style={{
                                                width: '32px',
                                                height: '32px',
                                                background: `${THEME.info}20`,
                                                color: THEME.info,
                                                borderRadius: '8px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                marginRight: SPACING.md,
                                                flexShrink: 0,
                                                fontSize: '12px',
                                                fontWeight: 'bold'
                                            }}>
                                                {idx + 1}
                                            </div>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{
                                                    fontSize: '14px',
                                                    fontWeight: '600',
                                                    color: THEME.secondary,
                                                    marginBottom: SPACING.xs
                                                }}>
                                                    {pred.disease || 'Unknown'}
                                                </div>
                                                {diseaseInfo && (
                                                    <div style={{
                                                        fontSize: '12px',
                                                        color: THEME.muted,
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: 'vertical',
                                                        overflow: 'hidden',
                                                        marginBottom: SPACING.xs
                                                    }}>
                                                        {diseaseInfo.description}
                                                    </div>
                                                )}
                                                <div style={{
                                                    display: 'flex',
                                                    gap: SPACING.sm,
                                                    alignItems: 'center',
                                                    fontSize: '11px'
                                                }}>
                                                    <div style={{
                                                        height: '4px',
                                                        flex: 1,
                                                        background: THEME.borderLight,
                                                        borderRadius: '10px',
                                                        overflow: 'hidden'
                                                    }}>
                                                        <div
                                                            style={{
                                                                height: '100%',
                                                                background: `linear-gradient(90deg, ${THEME.primary}, ${THEME.primaryLight})`,
                                                                borderRadius: '10px',
                                                                width: `${pred.confidence || 0}%`
                                                            }}
                                                        />
                                                    </div>
                                                    <span style={{
                                                        fontWeight: '700',
                                                        color: THEME.primary,
                                                        minWidth: '35px',
                                                        textAlign: 'right'
                                                    }}>
                                                        {Math.round(pred.confidence || 0)}%
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* ✨ NEW: DISEASE BLOG - DISPLAYS DISEASES BASED ON ANSWERS */}
                    <div style={{ marginBottom: SPACING.xxl }}>
                        <div style={{
                            fontSize: '13px',
                            fontWeight: '700',
                            color: THEME.muted,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            marginBottom: SPACING.md,
                            display: 'flex',
                            alignItems: 'center'
                        }}>
                            <span style={{
                                display: 'inline-block',
                                width: '8px',
                                height: '8px',
                                background: '#8b5cf6',
                                borderRadius: '50%',
                                marginRight: SPACING.sm
                            }}></span>
                            Disease Blog - Answer-based Predictions
                        </div>

                        <div style={{
                            background: THEME.white,
                            border: `2px solid ${THEME.border}`,
                            borderRadius: '16px',
                            padding: SPACING.xl,
                            display: 'grid',
                            gap: SPACING.lg
                        }}>
                            {(() => {
                                const answerEntries = Object.entries(userAnswers);

                                // Pre-collect all matching diseases to determine if we have data to show
                                const processedAnswers = answerEntries.map(([questionId, answerValue]) => {
                                    let answerDiseases = [];
                                    if (Array.isArray(answerValue)) {
                                        answerValue.forEach(val => {
                                            const mapped = ANSWER_DISEASE_MAPPING[val.toLowerCase()] || [];
                                            mapped.forEach(disease => {
                                                if (!answerDiseases.includes(disease)) {
                                                    answerDiseases.push(disease);
                                                }
                                            });
                                        });
                                    } else {
                                        answerDiseases = ANSWER_DISEASE_MAPPING[answerValue.toLowerCase()] || [];
                                    }
                                    return { questionId, answerValue, diseases: answerDiseases };
                                }).filter(item => item.diseases.length > 0);

                                if (processedAnswers.length === 0) {
                                    return (
                                        <div style={{
                                            padding: SPACING.lg,
                                            background: '#faf5ff',
                                            border: `1px solid #e9d5ff`,
                                            borderRadius: '12px',
                                            borderLeft: `4px solid #8b5cf6`
                                        }}>
                                            <div style={{
                                                fontSize: '14px',
                                                fontWeight: '700',
                                                color: '#7c3aed',
                                                marginBottom: SPACING.md,
                                                display: 'flex',
                                                alignItems: 'center'
                                            }}>
                                                Your symptoms overlap across multiple conditions, making it difficult to predict a specific disease—please consult a specialist.
                                            </div>
                                        </div>
                                    );
                                }

                                return processedAnswers.map((item, idx) => (
                                    <div key={idx} style={{
                                        padding: SPACING.lg,
                                        background: idx % 2 === 0 ? '#f5f3ff' : '#faf5ff',
                                        border: `1px solid #e9d5ff`,
                                        borderRadius: '12px',
                                        borderLeft: `4px solid #8b5cf6`
                                    }}>
                                        <div style={{
                                            fontSize: '14px',
                                            fontWeight: '700',
                                            color: '#7c3aed',
                                            marginBottom: SPACING.md,
                                            display: 'flex',
                                            alignItems: 'center'
                                        }}>
                                            <Icons.BookOpen style={{ marginRight: SPACING.sm, color: '#8b5cf6' }} />
                                            <span>
                                                {Array.isArray(item.answerValue)
                                                    ? `Selected: ${item.answerValue.join(', ')}`
                                                    : `Selected: ${item.answerValue}`
                                                }
                                            </span>
                                        </div>

                                        <div style={{
                                            fontSize: '12px',
                                            color: THEME.muted,
                                            marginBottom: SPACING.md,
                                            fontStyle: 'italic'
                                        }}>
                                            Based on this answer, the following diseases are possible:
                                        </div>

                                        <div style={{
                                            display: 'grid',
                                            gap: SPACING.sm
                                        }}>
                                            {item.diseases.map((disease, didx) => {
                                                const diseaseInfo = DISEASE_DATABASE[disease];
                                                return (
                                                    <div key={didx} style={{
                                                        padding: SPACING.md,
                                                        background: THEME.white,
                                                        border: `1px solid #e9d5ff`,
                                                        borderRadius: '8px',
                                                        borderLeft: `3px solid #8b5cf6`
                                                    }}>
                                                        <div style={{
                                                            fontSize: '13px',
                                                            fontWeight: '600',
                                                            color: THEME.secondary,
                                                            marginBottom: SPACING.xs
                                                        }}>
                                                            {didx + 1}. {disease}
                                                        </div>
                                                        {diseaseInfo && (
                                                            <div style={{
                                                                fontSize: '12px',
                                                                color: THEME.muted,
                                                                lineHeight: '1.5',
                                                                marginBottom: SPACING.xs
                                                            }}>
                                                                {diseaseInfo.description}
                                                            </div>
                                                        )}
                                                        {diseaseInfo && (
                                                            <div style={{
                                                                display: 'flex',
                                                                gap: SPACING.xs,
                                                                flexWrap: 'wrap',
                                                                marginTop: SPACING.xs
                                                            }}>
                                                                <span style={{
                                                                    fontSize: '10px',
                                                                    background: '#f0f9ff',
                                                                    color: THEME.info,
                                                                    padding: `${SPACING.xs} ${SPACING.sm}`,
                                                                    borderRadius: '6px',
                                                                    fontWeight: '500'
                                                                }}>
                                                                    Severity: {diseaseInfo.severity || 'Unknown'}
                                                                </span>
                                                                <span style={{
                                                                    fontSize: '10px',
                                                                    background: '#fef3c7',
                                                                    color: '#d97706',
                                                                    padding: `${SPACING.xs} ${SPACING.sm}`,
                                                                    borderRadius: '6px',
                                                                    fontWeight: '500'
                                                                }}>
                                                                    Part: {diseaseInfo.bodyPart || 'General'}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ));
                            })()}
                        </div>
                    </div>

                    {/* SPECIALIST RECOMMENDATIONS */}
                    <div style={{ marginBottom: SPACING.xxl }}>
                        <div style={{
                            fontSize: '13px',
                            fontWeight: '700',
                            color: THEME.muted,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            marginBottom: SPACING.md
                        }}>Recommended Health Specialists</div>
                        {specialists.map((specialist, idx) => (
                            <div key={idx} style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                padding: SPACING.lg,
                                background: idx === 0 ? `linear-gradient(135deg, ${THEME.primarySofter}, ${THEME.primarySoft})` : THEME.backgroundLighter,
                                border: idx === 0 ? `2px solid ${THEME.primary}` : `1px solid ${THEME.border}`,
                                borderRadius: '12px',
                                marginBottom: SPACING.md
                            }}>
                                <div style={{
                                    width: '44px',
                                    height: '44px',
                                    background: idx === 0 ? THEME.primary : THEME.backgroundLight,
                                    color: idx === 0 ? 'white' : THEME.secondary,
                                    borderRadius: '10px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginRight: SPACING.lg,
                                    flexShrink: 0,
                                    fontSize: '20px',
                                    fontWeight: 'bold'
                                }}>
                                    {idx === 0 ? '★' : idx + 1}
                                </div>
                                <div style={{ flex: 1 }}>
                                    <div style={{
                                        fontSize: '15px',
                                        fontWeight: '700',
                                        color: THEME.secondary,
                                        marginBottom: SPACING.xs
                                    }}>{specialist.title}</div>
                                    <div style={{
                                        fontSize: '13px',
                                        color: THEME.muted,
                                        lineHeight: '1.4'
                                    }}>{specialist.specialty}</div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* RECOMMENDATIONS */}
                    {recommendations && recommendations.primary_care && (
                        <div style={{ marginBottom: SPACING.xxl }}>
                            <div style={{
                                fontSize: '13px',
                                fontWeight: '700',
                                color: THEME.muted,
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                marginBottom: SPACING.md
                            }}>Medical Recommendations</div>
                            {Array.isArray(recommendations.primary_care) && recommendations.primary_care.map((rec, idx) => (
                                <div key={`care-${idx}`} style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    padding: SPACING.md,
                                    background: THEME.primarySofter,
                                    borderRadius: '12px',
                                    marginBottom: SPACING.md
                                }}>
                                    <div style={{
                                        width: '32px',
                                        height: '32px',
                                        background: THEME.primary,
                                        color: 'white',
                                        borderRadius: '8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        marginRight: SPACING.md,
                                        flexShrink: 0,
                                        fontWeight: 'bold'
                                    }}>
                                        {idx + 1}
                                    </div>
                                    <div style={{
                                        fontSize: '13px',
                                        color: THEME.secondary,
                                        lineHeight: '1.5'
                                    }}>{rec}</div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* DISCLAIMER */}
                    <div style={{
                        padding: SPACING.lg,
                        background: '#fef3c7',
                        border: '1px solid #fcd34d',
                        borderRadius: '12px',
                        fontSize: '12px',
                        color: '#92400e',
                        lineHeight: '1.6'
                    }}>
                        <strong>⚠️ Medical Disclaimer:</strong> This assessment provides AI-generated predictions for educational purposes only. It does NOT replace professional medical diagnosis or treatment. Always consult qualified healthcare providers for medical advice, diagnosis, and treatment.
                    </div>
                </div>

                <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr',
                    gap: SPACING.md,
                    paddingTop: SPACING.xxl,
                    borderTop: `1px solid ${THEME.border}`
                }}>
                    <button
                        onClick={startNewAssessment}
                        style={{
                            padding: `${SPACING.md} ${SPACING.xl}`,
                            fontSize: '14px',
                            fontWeight: '600',
                            borderRadius: '10px',
                            background: THEME.backgroundLight,
                            color: THEME.secondary,
                            border: `1px solid ${THEME.border}`,
                            cursor: 'pointer',
                            transition: 'all 0.3s'
                        }}
                    >
                        New Assessment
                    </button>
                    <button
                        onClick={handleDownloadReport}
                        style={{
                            padding: `${SPACING.md} ${SPACING.xl}`,
                            fontSize: '14px',
                            fontWeight: '600',
                            borderRadius: '10px',
                            background: THEME.info,
                            color: 'white',
                            border: 'none',
                            cursor: 'pointer',
                            boxShadow: THEME.shadowMd,
                            transition: 'all 0.3s'
                        }}
                    >
                        Download Report
                    </button>
                    <button
                        onClick={onHide}
                        style={{
                            padding: `${SPACING.md} ${SPACING.xl}`,
                            fontSize: '14px',
                            fontWeight: '600',
                            borderRadius: '10px',
                            background: `linear-gradient(135deg, ${THEME.primary}, ${THEME.primaryLight})`,
                            color: 'white',
                            border: 'none',
                            cursor: 'pointer',
                            boxShadow: THEME.shadowMd,
                            transition: 'all 0.3s'
                        }}
                    >
                        Close
                    </button>
                </div>
            </div>
        );
    };

    // MAIN RENDER
    return (
        <ErrorBoundary>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
                * { font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
                .modal-content { background: ${THEME.white}; border: none; border-radius: 20px !important; box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important; }
                .modal-header { border-bottom: 1px solid ${THEME.border}; background: linear-gradient(135deg, ${THEME.primarySofter}, ${THEME.white}); }
                .modal-body { background: ${THEME.white}; }
            `}</style>

            <Modal
                show={show}
                onHide={onHide}
                centered
                size="lg"
                backdrop="static"
                keyboard={false}
                contentClassName="border-0 shadow-lg rounded-4"
                style={{ zIndex: 1050 }}
                dialogClassName="modal-xl"
            >
                <Modal.Header closeButton className="border-0" style={{ padding: SPACING.xl, background: `linear-gradient(135deg, ${THEME.primarySofter}, ${THEME.white})` }}>
                    <div className="d-flex align-items-center w-100">
                        {(view === 'questions' || view === 'results') && (
                            <Button
                                variant="link"
                                className="p-0 text-decoration-none me-3"
                                onClick={() => {
                                    if (view === 'questions') {
                                        setView('bodypart');
                                        setBodyPartData(null);
                                    }
                                }}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    color: THEME.muted
                                }}
                            >
                                <span style={{ marginRight: SPACING.sm }}>←</span>
                                <span>Back</span>
                            </Button>
                        )}
                        <h5 className="mb-0" style={{
                            fontWeight: 'bold',
                            color: THEME.secondary,
                            fontSize: '18px'
                        }}>
                            Health Prediction System
                        </h5>
                    </div>
                </Modal.Header>

                <Modal.Body
                    style={{
                        height: '70vh',
                        minHeight: '680px',
                        maxHeight: '90vh',
                        display: 'flex',
                        flexDirection: 'column',
                        background: THEME.white,
                        padding: SPACING.xl,
                        overflowY: 'auto'
                    }}
                >
                    {!isSystemValid && errorMessage ? (
                        <div style={{ textAlign: 'center', padding: SPACING.xxxl, color: THEME.secondary }}>
                            <Icons.AlertTriangle style={{ color: THEME.danger, marginBottom: SPACING.lg }} />
                            <h3>Configuration Error</h3>
                            <p style={{ color: THEME.muted }}>{errorMessage}</p>
                        </div>
                    ) : view === 'intro' ? (
                        renderIntroScreen()
                    ) : view === 'bodypart' ? (
                        renderBodyPartSelection()
                    ) : view === 'questions' ? (
                        renderQuestionnaire()
                    ) : view === 'analyzing' ? (
                        renderAnalyzingScreen()
                    ) : view === 'results' ? (
                        renderResults()
                    ) : (
                        <div style={{ textAlign: 'center', padding: SPACING.xxxl }}>Loading...</div>
                    )}
                </Modal.Body>
            </Modal>
        </ErrorBoundary>
    );
};

export default HealthCheck;
